import { Component, OnInit, HostListener, inject } from '@angular/core';
import {
  Event as NavigationEvent,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Params,
  Router,
} from '@angular/router';
import { AccessType } from '@app/@shared/enums/access-types.enum';
import { SubdomainTypes } from '@app/@shared/enums/subdomain-types.enum';
import { ApplicationProfile } from '@app/@shared/models/application-profile.model';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';
import { SubdomainService } from '@app/@shared/services/subdomain.service';
import { CredentialsService } from '@app/auth';
import { isNotNullOrUndefined } from '@ep/shared';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, debounceTime, filter, Subscription, tap } from 'rxjs';
import { WindowService } from '@app/resources/services/window.service';
import { TimezoneService } from '@app/resources/services/timezone.service';
import { BreadcrumbService } from '@app/resources/services/breadcrumb.service';
import { FONT_AWESOME_ICONS } from '@shared/icons';
import { Store } from '@ngrx/store';
import { ApplicationProfileActions, CredentialsActions } from '@app/resources/ngrx/actions';
import { SignalRService } from '@app/resources/services/signalr.service';
import { SignalrTargetsEnum } from '@shared/enums';
import { AccordionTab } from 'primeng/accordion';

@UntilDestroy()
@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent implements OnInit {
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly windowService = inject(WindowService);
  private readonly timezoneService = inject(TimezoneService);
  private readonly signalRService = inject(SignalRService);
  private readonly store = inject(Store);
  private readonly cssValidatorService = inject(CssValidatorService);
  // Icons
  icons = FONT_AWESOME_ICONS;

  // Component State.
  isLoading: boolean = false;
  showMobileDropdown: boolean = false;

  // Application Data.
  AccessType = AccessType;
  applicationProfile!: ApplicationProfile;
  subdomainType!: SubdomainTypes | null;
  SubdomainTypes = SubdomainTypes;

  activeMenuAccordionIndex: number = 0;
  listQueryParams: { [area: string]: Params } = {};

  subs = new Subscription();

  constructor(
    private router: Router,
    private applicationProfileService: ApplicationProfileService,
    private credentialsService: CredentialsService,
    private subdomainService: SubdomainService
  ) {}

  private _resize$ = new BehaviorSubject<boolean>(false);
  readonly resize$ = this._resize$.asObservable().pipe(filter(isNotNullOrUndefined));

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this._resize$.next(true);
  }

  ngOnInit(): void {
    this.signalRService.hubConnection.on(SignalrTargetsEnum.onJwtChange, (credentials: any) => {
      this.credentialsService.setCredentials(credentials);
      this.store.dispatch(CredentialsActions.updateCredentials({ payload: { credentials } }));
    });

    this.subs.add(
      this.resize$
        .pipe(
          debounceTime(300),
          tap(() => {
            this.windowService.checkScreenSize();
            document.documentElement.style.setProperty('--screenHeight', `${window.innerHeight}px`);
          })
        )
        .subscribe()
    );

    this.subdomainType = this.subdomainService.getDomain();
    this.windowService.checkScreenSize();
    this.subscribeToApplicationProfile();

    this.subs.add(
      this.router.events
        .pipe(
          filter(
            (event: NavigationEvent) =>
              event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError
          )
        )
        .subscribe((event) => {
          this.isLoading = false;
          if (event instanceof NavigationEnd) {
            this.updateNavigation();
          }
        })
    );

    this.updateNavigation();
    this.manageMenuTabs();
  }

  private updateNavigation(): void {
    this.breadcrumbService.updateBreadcrumbs(this.applicationProfile.Location.LocationId);
    this.listQueryParams = this.breadcrumbService.listQueryParamsByArea();
  }

  get isMobileScreen() {
    return this.windowService.isMobileScreen;
  }

  get isSmallScreen() {
    return this.windowService.isSmallScreen;
  }

  get shouldShowFullWidth() {
    return this.windowService.shouldShowFullWidth;
  }

  set shouldShowFullWidth(showFullWidth: boolean) {
    this.windowService.setShowFullWidth(showFullWidth);
  }

  onCloseClicked(): void {
    this.shouldShowFullWidth = !this.shouldShowFullWidth;
  }

  onNavigation(resource: string) {
    this.isLoading = true;
    this.router.navigateByUrl(`/${this.applicationProfile.Location.LocationId}/${resource}`);
  }

  onToggleMobileDropdown(): void {
    this.showMobileDropdown = !this.showMobileDropdown;
  }

  closeMobileDropdown(): void {
    this.showMobileDropdown = false;
  }

  shouldShowMenuItem(accessType: AccessType): boolean {
    return this.credentialsService.hasAccessAny([accessType]);
  }

  getImageSrc(): string {
    const useSmallLogo = (this.isSmallScreen && !this.shouldShowFullWidth) || !this.shouldShowFullWidth;

    // Try to use theme logos if available
    if (this.applicationProfile?.PortalTheme) {
      const logo = useSmallLogo
        ? this.applicationProfile.PortalTheme.SecondaryLogo
        : this.applicationProfile.PortalTheme.PrimaryLogo;

      if (logo) {
        return logo;
      }
    }

    // Fall back to default logos
    const prefix = useSmallLogo ? 'small-' : '';
    const suffix = this.subdomainType === SubdomainTypes.EagleProcessing ? 'eagle-logo.png' : 'enroll-and-pay-logo.png';

    return `../../assets/${prefix}${suffix}`;
  }

  private subscribeToApplicationProfile(): void {
    this.applicationProfileService.applicationProfile$
      .pipe(untilDestroyed(this))
      .subscribe((applicationProfile: ApplicationProfile) => {
        this.applicationProfile = applicationProfile;
        this.timezoneService.initializeTimezone(this.applicationProfile.Location.TimeZoneType);
        this.store.dispatch(
          ApplicationProfileActions.loadApplicationProfilesSuccess({ payload: { profile: applicationProfile } })
        );

        // Set CSS custom properties for theming
        this.setCSSCustomProperties();
      });
  }

  private setCSSCustomProperties(): void {
    if (this.applicationProfile?.PortalTheme?.Theme) {
      const theme = this.applicationProfile.PortalTheme.Theme;
      // Set CSS custom properties
      document.documentElement.style.setProperty('--sidebar-bg-primary', theme.sidebar?.backgroundColor?.primary);
      document.documentElement.style.setProperty('--sidebar-bg-secondary', theme.sidebar?.backgroundColor?.secondary);
      document.documentElement.style.setProperty('--sidebar-bg-active', theme.sidebar?.backgroundColor?.active);
      document.documentElement.style.setProperty('--sidebar-bg-dropdown', theme.sidebar?.backgroundColor?.dropdown);
      document.documentElement.style.setProperty('--sidebar-text-color', theme.sidebar?.textColor);
      document.documentElement.style.setProperty('--sidebar-logo-bg', theme.sidebar?.logoBackgroundColor);
      document.documentElement.style.setProperty('--header-bg-primary', theme.header?.backgroundColor);
      document.documentElement.style.setProperty('--header-primary', theme.header?.primary);
      document.documentElement.style.setProperty('--main-content-bg', theme.backgroundColor);

      // Check for undefined CSS variables
      this.cssValidatorService.checkUndefinedCssVariables('ShellComponent');
    }
  }

  private manageMenuTabs(): void {
    // TODO when we have the Merchant Accordion, we need to change this code to:
    // this.activeMenuAccordionIndex = this.router.url.indexOf('admin') === -1 ? 0 : 1;
    this.activeMenuAccordionIndex = this.router.url.indexOf('admin') === -1 ? 1 : 0;
  }
}
